import axios from 'axios';
import { PostParams } from '@/models/params/postParams';
import { RelatedPostsParams } from '@/models/params/relatedPostsParams';
import { StateInput, StoreUtils } from '@/store/storeUtils';
import objectHash from 'object-hash';
import qs from 'qs';
import { StoreObject, StoreObjectList } from '@/store/storeObject';
import { Post } from '@/models/dtos';
import { StoreModule } from '@/store/storeModule';
import { StateObject } from '@/store/stateObject';
import friggUtils from '@/utils/friggUtils';

export default {
	state: {
		// Dictionary with post.id as key
		dictionary: {} as {[key: number]: StoreObject<Post>},
		// Params dictionary with all params as key, and list of posts as value
		paramsDictionary: {} as {[key: string]: StoreObjectList<Post>},
	},
	actions: {
		loadPosts({commit, state}: any, params: PostParams | RelatedPostsParams) {
			const params2 = StoreUtils.getParams(params, state);
			const hash = objectHash(params2);
			// Already exists, therefore returning
			if (StoreUtils.getStoreObjectListByParamHash(state, hash) != null) {
				return;
			}

			commit('API_POSTS_PENDING', hash);
			return axios.get(friggUtils.getUrl('/home/posts'), {
				params: params2,
			}).then((response) => {
				commit('API_POSTS_SUCCESS', {paramsHash: hash, data: response.data});
			}).catch((error) => {
				commit('API_POSTS_ERROR', {paramsHash: hash, error});
			});
		},
		loadPostById({commit, state}: any, postId: number) {
			if (StoreUtils.getStoreObjectById(state, postId) != null) {
				return;
			}
			commit('API_POST_PENDING', postId);
			return axios.get(friggUtils.getUrl(`/home/posts/${postId}`), {
				params: StoreUtils.getParams({postId}, state),
			}).then((response: any) => {
				commit('API_POST_SUCCESS', {id: postId, data: response.data});
			}).catch((error) => {
				commit('API_POST_ERROR', {id: postId, error});
			});
		},
		loadRelatedPosts({commit, state}: any, params: RelatedPostsParams) {
			const params2 = StoreUtils.getParams(params, state);
			const hash = objectHash(params2);
			// Already exists, therefore returning
			if (StoreUtils.getStoreObjectListByParamHash(state, hash) != null) {
				return;
			}

			commit('API_POSTS_PENDING', hash);
			return axios.get(friggUtils.getUrl(`/home/posts/${params.postId}/related`), {
				params: params2,
				paramsSerializer: (p) => {
					return qs.stringify(p);
				},
			}).then((response) => {
				commit('API_POSTS_SUCCESS', {paramsHash: hash, data: response.data});
			}).catch((error) => {
				commit('API_POSTS_ERROR', {paramsHash: hash, error});
			});
		},
	},
	mutations: {
		API_POSTS_PENDING(state: any, paramsHash: string) {
			StoreUtils.setPending<Post>(state, new StateInput(paramsHash));
		},
		API_POSTS_SUCCESS(state: any, d: {data: any, paramsHash: string}) {
			StoreUtils.setSuccess<Post>(state, d.data, new StateInput(d.paramsHash));
		},
		API_POSTS_ERROR(state: any, d: {paramsHash: string, error: any}) {
			StoreUtils.setError<Post>(state, d.error, new StateInput(d.paramsHash));
		},
		API_POST_PENDING(state: any, id: number) {
			StoreUtils.setPending<Post>(state, new StateInput(null, id));
		},
		API_POST_SUCCESS(state: any, d: {data: any, id: number}) {
			StoreUtils.setSuccess<Post>(state, d.data, new StateInput(null, d.id));
		},
		API_POST_ERROR(state: any, d: {id: string, error: any}) {
			StoreUtils.setError<Post>(state, d.error, new StateInput(null, d.id));
		},
	},
	getters: {
		/**
		 * Gets post by id
		 */
		postById: (state: StateObject<Post>) => (postId: number): StoreObject<Post> => {
			return StoreUtils.getStoreObjectById<Post>(state, postId);
		},

		/**
		 * Gets posts by parameters
		 */
		postsByParams: (state: StateObject<Post>) => (params: PostParams): StoreObjectList<Post> => {
			return StoreUtils.getStoreObjectListByParamHash<Post>(state, objectHash(params));
		},
	},
} as StoreModule<Post>;
